/* eslint no-underscore-dangle: 0 */
class SuggestionsDropdown {
    constructor (inputEle, fetch, abort, {
        contentGetter,
        onsubmit,
        onshow,
        onclose,
        timeout = 200,
        dropdownClass = '',
        selectedClass = 'suggestion-dropdown-item-selected',
        statusClass = 'u-screen-reader-only dropdown-status',
        updateValue,
        hideOnDeactivation,
        insertionElement,
    } = {}) {
        if (!inputEle) {
            throw Error('An input element must be specified.');
        }

        this.fetch = fetch;
        this.abort = abort;
        this.contentGetter = contentGetter || this._contentGetterDefault;
        this.onsubmit = onsubmit;
        this.onshow = onshow;
        this.onclose = onclose;
        this._timeout = timeout;
        this._dropdownClass = dropdownClass;
        this._selectedClass = selectedClass;
        this._statusClass = statusClass;
        this._updateValue = updateValue !== false;
        this._hideOnDeactivation = hideOnDeactivation !== false;
        this._insertionElement = insertionElement;

        this._inputEle = inputEle;
        this._lastEle = null;
        this._dropdownEle = null;
        this._statusEle = null;
        this._selectedItem = null;
        this._timeoutId = null;
        this._navItems = null;
        this._navItemIndex = null;

        this._inputEle.setAttribute('role', 'combobox');
        this._inputEle.setAttribute('aria-autocomplete', 'list');
        this._inputEle.setAttribute('aria-expanded', 'false');

        this.show = this.show.bind(this);
        this.hide = this.hide.bind(this);
        this._handleInput = this._handleInput.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._handleFocus = this._handleFocus.bind(this);
        this._handleClick = this._handleClick.bind(this);
        this._handleTouchEnd = this._handleTouchEnd.bind(this);

        inputEle.addEventListener('input', this._handleInput);
        inputEle.addEventListener('keydown', this._handleKeyDown);
        inputEle.addEventListener('focus', this._handleFocus);
        inputEle.addEventListener('touchend', this._handleTouchEnd);
    }

    /**
     * TODO
     * @parameter term
     * @parameter callback
     */
    fetch () {}

    /**
     * TODO
     */
    abort () {}

    /**
     * Getter for the content that should be passed to onsubmit.
     * @parameter itemEle
     */
    contentGetter () {}

    /**
     * TODO
     * @parameter item
     * @parameter itemEle
     */
    onsubmit () {}

    /**
     * Called every time show() is run
     */
    onshow () {}

    /**
     * Called every time close() is run
     */
    onclose () {}

    show (ele) {
        if (!ele || document.activeElement !== this._inputEle) {
            return;
        }

        this._lastEle = ele;

        if (!this._dropdownEle) {
            this._dropdownEle = document.createElement('div');
            this._dropdownEle.className = this._dropdownClass;
            this._dropdownEle.setAttribute('role', 'listbox');
            this._dropdownEle.id = `listbox-${Math.floor(Math.random() * 0xFFFFFFF)}`;
        }

        while (this._dropdownEle.firstChild) {
            this._dropdownEle.firstChild.remove();
        }

        this._dropdownEle.append(ele);

        this._inputEle.setAttribute('aria-expanded', 'true');

        // Reset the selected item in case one exists when show() is called
        this._selectedItem = null;
        this._inputEle.removeAttribute('aria-activedescendant');
        this._inputEle.removeAttribute('aria-owns');

        this._navItems = this._dropdownEle.querySelectorAll('.selectable__list__item');
        // eslint-disable-next-line no-shadow
        [...this._navItems].forEach((ele) => {
            if (!ele.hasAttribute('role')) {
                ele.setAttribute('role', 'option');
            }
            if (!ele.id) {
                // eslint-disable-next-line no-param-reassign
                ele.id = `option-${Math.floor(Math.random() * 0xFFFFFFF)}`;
            }
        });

        if (this._insertionElement) {
            this._insertionElement.append(this._dropdownEle);
        } else {
            this._inputEle.after(this._dropdownEle);
        }

        // If we call show() when the dropdown is already shown (e.g. when
        // streaming results), we select the same index again.
        if (this._navItemIndex != null) {
            this.highlightItem(this._navItemIndex);
        }

        document.addEventListener('click', this._handleClick, true);
        // eslint-disable-next-line no-shadow
        [...document.querySelectorAll('iframe')].forEach(ele => {
            // eslint-disable-next-line no-param-reassign
            ele.style.pointerEvents = 'none';
        });
        if (this.onshow) {
            this.onshow();
        }
    }

    hide () {
        if (this._statusEle) {
            this._statusEle.remove();
            this._statusEle = null;
        }
        if (!this._dropdownEle) {
            return;
        }
        this._dropdownEle.remove();
        this._inputEle.setAttribute('aria-expanded', 'false');
        this._inputEle.removeAttribute('aria-owns');
        this._inputEle.removeAttribute('aria-activedescendant');
        this._dropdownEle = null;
        this._timeoutId = null;
        this._navItems = null;
        this._navItemIndex = null;
        if (this._selectedItem) {
            this._selectedItem.classList.remove(this._selectedClass);
            this._selectedItem = null;
        }
        document.removeEventListener('click', this._handleClick, true);
        [...document.querySelectorAll('iframe')].forEach(ele => {
            // eslint-disable-next-line no-param-reassign
            ele.style.pointerEvents = '';
        });
        if (this.onclose) {
            this.onclose();
        }
    }

    _handleTouchEnd () {
        this._handleFocus();
    }

    _handleFocus () {
        if (this._lastEle) {
            this.show(this._lastEle);
        } else {
            this._handleInput()
        }
    }

    _handleInput () {
        const {value} = this._inputEle;
        const term = value.replace(/\s+/g, ' ').trim();
        this._navItemIndex = null; // reset so that nothing gets selected on show()
        this._inputEle.setAttribute('value', value); // For getting defaultValue later
        this.abort();
        window.clearTimeout(this._timeoutId);
        this._timeoutId = window.setTimeout(() => {
            this.fetch(term, this.show.bind(this));
        }, this._timeout);
    }

    _setValue (value) {
        if (this._updateValue) {
            this._inputEle.value = value;
        }
    }

    _handleKeyDown (event) {
        if (event.metaKey || event.altKey || event.ctrlKey) {
            return;
        }

        switch (this._getKey(event)) {
            case 'Tab':
                if (this._hideOnDeactivation) {
                    this.hide();
                }
                event.stopPropagation();
                return;

            case 'Enter': {
                const isHandled = this._submitSelection();
                if (!isHandled) {
                    this.hide();
                    this._inputEle.blur();
                    return;
                }
                break;
            }

            case 'Escape':
                this._inputEle.value = this._inputEle.defaultValue;
                if (!this._selectedItem) {
                    this._inputEle.select();
                }
                this.hide();
                break;

            case 'ArrowUp':
                this._selectPrevious();
                break;

            case 'ArrowDown':
                if (this._navItems) {
                    this._selectNext();
                } else if (this._lastEle) {
                    this.show(this._lastEle);
                }
                break;

            case 'PageUp':
                this._selectFirst();
                break;

            case 'PageDown':
                this._selectLast();
                break;

            default:
                return;
        }
        event.stopPropagation();
        event.preventDefault();
    }

    _handleClick (event) {
        if (event.button !== 0 || event.metaKey || event.shiftKey || event.ctrlKey || event.altKey) {
            return;
        }
        if(event.button === 0 && event.target.id === 'remove-last-searches-button') { // identify remove last searches event and let it pass
            return;
        }
        if (event.target === this._inputEle || event.target === this._dropdownEle) {
            return;
        }
        if (this._dropdownEle.contains(event.target)) {
            event.stopPropagation();
            event.preventDefault();

            const item = event.target.closest('.selectable__list__item');
            if (!item) {
                return;
            }
            this._selectedItem = item;
            const isHandled = this._submitSelection();
            if (isHandled) {
                return;
            }
            this._inputEle.blur();
        }

        if (this._hideOnDeactivation) {
            this.hide();
        }
    }

    highlightItem (index) {
        if (!this._navItems) { return; }

        if (this._navItemIndex != null) {
            const item = this._navItems[this._navItemIndex];
            item.classList.remove(this._selectedClass);
            item.removeAttribute('aria-selected');
        }

        this._navItemIndex = index;
        this._selectedItem = this._navItems[index];

        if (!this._selectedItem) {
            this._inputEle.removeAttribute('aria-activedescendant');
            this._inputEle.removeAttribute('aria-owns');
            this._inputEle.value = this._inputEle.defaultValue;
            this._navItemIndex = null;
            return;
        }

        this._inputEle.setAttribute('aria-owns', this._dropdownEle.id);
        this._inputEle.setAttribute('aria-activedescendant', this._selectedItem.id);
        this._selectedItem.setAttribute('aria-selected', 'true');
        this._selectedItem.classList.add(this._selectedClass);
    }

    setStatus (text) {
        if (!this._dropdownEle) {
            return;
        }
        if (!this._statusEle) {
            this._statusEle = document.createElement('h2');
            this._statusEle.className = this._statusClass;
            this._statusEle.setAttribute('role', 'status');
            this._dropdownEle.before(this._statusEle);
        }
        this._statusEle.textContent = text;
    }

    _selectItem (index) {
        this.highlightItem(index);
        if (this._selectedItem) {
            this._setValue(this._getSelectedItemContent());
        }
    }

    _selectFirst () {
        if (!this._navItems) {
            return;
        }
        this._selectItem(0);
    }

    _selectLast () {
        if (!this._navItems) {
            return;
        }
        this._selectItem(this._navItems.length - 1);
    }

    _selectPrevious () {
        if (!this._navItems) {
            return;
        }
        const index = this._navItemIndex == null ? this._navItems.length - 1 : this._navItemIndex - 1;
        this._selectItem(index);
    }

    _selectNext () {
        if (!this._navItems) {
            return;
        }
        const index = this._navItemIndex == null ? 0 : this._navItemIndex + 1;
        this._selectItem(index);
    }

    _submitSelection () {
        let isHandled = false;
        
        if (this.onsubmit) {
            isHandled = this.onsubmit(this._inputEle.value, this._selectedItem);
        }
        
        const content = this._selectedItem ? this._getSelectedItemContent() : this._inputEle.value;
        this._setValue(content);

        return Boolean(isHandled);
    }

    _getSelectedItemContent () {
        return this.contentGetter(this._selectedItem);
    }

    _contentGetterDefault (ele) {
        return ele.textContent;
    }

    _getStringLength (string) {
        // https://mathiasbynens.be/notes/javascript-unicode#accounting-for-astral-symbols
        return string
            .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '_')
            .length;
    }

    _getKey (event) {
        const {key} = event;
        if (key) {
            return {
                // These are renamed in the spec, but Firefox
                // still have the old names (and warns about it)
                Del: 'Delete',
                Esc: 'Escape',
                Left: 'ArrowLeft',
                Up: 'ArrowUp',
                Right: 'ArrowRight',
                Down: 'ArrowDown',
            }[key] || key;
        }

        // Fall back to event.keyCode
        const {keyCode} = event;
        return {
            9: 'Tab',
            13: 'Enter',
            27: 'Escape',
            33: 'PageUp',
            34: 'PageDown',
            37: 'ArrowLeft',
            38: 'ArrowUp',
            39: 'ArrowRight',
            40: 'ArrowDown',
        }[keyCode] || keyCode;
    }
}

export default SuggestionsDropdown;
